'use client';

import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ReferenceLine } from 'recharts';
import { HistoricalDataPoint } from '@/types';
import { useChartColors } from '@/lib/useChartColors';
import { CandlestickChart, type Candle } from './CandlestickChart';
import { useChartType } from './ChartTypeContext';

interface LotPriceLine {
  price: number;
  label: string;
}

interface StockChartProps {
  data: HistoricalDataPoint[];
  color?: string;
  lotPriceLines?: LotPriceLine[];
  avgPriceLine?: number;
  isIntraday?: boolean;
  timeRange?: string;
}

type ChartMode = 'line' | 'candle';

export const StockChart: React.FC<StockChartProps> = ({ data, color = "#3b82f6", lotPriceLines = [], avgPriceLine, isIntraday = false, timeRange }) => {
  const chartColors = useChartColors();
  const { chartType } = useChartType();
  // null = follow persisted preference; once the user clicks the toggle in this
  // session, that choice wins until the page is reloaded.
  const [override, setOverride] = useState<ChartMode | null>(null);
  const mode: ChartMode = override ?? chartType;
  const setMode = (m: ChartMode) => setOverride(m);

  const candleData = useMemo<Candle[]>(() => {
    if (!data) return [];
    return data
      .filter((d) => d.open != null && d.high != null && d.low != null && d.close != null)
      .map((d) => ({
        time: new Date(d.date).getTime(),
        open: d.open as number,
        high: d.high as number,
        low: d.low as number,
        close: d.close as number,
        volume: d.volume,
      }));
  }, [data]);

  const hasCandleData = candleData.length >= 2;
  // Derive what's actually rendered. If the user picked candle but the current
  // range has no OHLC, fall back to line for both the chart AND the toggle UI
  // so the active button reflects what's on screen.
  const effectiveMode: ChartMode = mode === 'candle' && hasCandleData ? 'candle' : 'line';

  // For intraday, convert date strings (ISO timestamps) to epoch ms
  const chartData = useMemo(() => {
    if (isIntraday) {
      return data.map(d => ({
        ...d,
        date: new Date(d.date).getTime(),
      }));
    }
    return data;
  }, [data, isIntraday]);

  // Compute market open/close reference lines for intraday
  const marketLines = useMemo(() => {
    if (!isIntraday || chartData.length === 0) return null;

    const firstPoint = new Date(chartData[0].date);
    const year = firstPoint.getUTCFullYear();
    const month = firstPoint.getUTCMonth();
    const day = firstPoint.getUTCDate();

    const testDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
    const etFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false });
    const etHour = parseInt(etFormatter.format(testDate), 10);
    const utcOffset = 12 - etHour;

    const marketOpenUTC = Date.UTC(year, month, day, 9 + utcOffset, 30, 0);
    const marketCloseUTC = Date.UTC(year, month, day, 16 + utcOffset, 0, 0);

    return { marketOpen: marketOpenUTC, marketClose: marketCloseUTC };
  }, [isIntraday, chartData]);

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-xl" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>No historical data available</p>
      </div>
    );
  }

  const chartPrices = data.map(d => d.price);
  const allPrices = [
    ...chartPrices,
    ...lotPriceLines.map(l => l.price),
    ...(avgPriceLine !== undefined ? [avgPriceLine] : [])
  ];
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const padding = maxPrice !== minPrice ? (maxPrice - minPrice) * 0.1 : Math.abs(maxPrice) * 0.1 || 1;

  const formatTime = (epochMs: number) => {
    const d = new Date(epochMs);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    });
  };

  const formatDateTick = (val: string) => {
    const d = new Date(val);
    if (timeRange === '1W') return `${d.getMonth() + 1}/${d.getDate()}`;
    if (timeRange === '1M' || timeRange === '3M') return `${d.getMonth() + 1}/${d.getDate()}`;
    if (timeRange === '6M' || timeRange === 'YTD' || timeRange === '1Y') return `${d.getMonth() + 1}/${d.getFullYear().toString().substr(2)}`;
    if (timeRange === '2Y' || timeRange === '5Y' || timeRange === '10Y' || timeRange === 'ALL') return `${d.getMonth() + 1}/${d.getFullYear().toString().substr(2)}`;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const toggle = (
    <div
      className="mb-2 inline-flex rounded-full p-0.5"
      style={{ backgroundColor: 'var(--color-toggle-bg)' }}
    >
      {(['line', 'candle'] as const).map((m) => {
        const disabled = m === 'candle' && !hasCandleData;
        return (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => setMode(m)}
            title={disabled ? 'Candle data unavailable for this range' : undefined}
            className="px-3 py-0.5 text-xs font-medium rounded-full transition-all disabled:cursor-not-allowed disabled:opacity-50"
            style={
              effectiveMode === m
                ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' }
                : { color: 'var(--color-text-secondary)' }
            }
          >
            {m === 'line' ? 'Line' : 'Candle'}
          </button>
        );
      })}
    </div>
  );

  if (effectiveMode === 'candle') {
    return (
      <div className="w-full">
        {toggle}
        <CandlestickChart
          key={timeRange ?? 'default'}
          data={candleData}
          height={256}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      {toggle}
      <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height={256}>
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
          <defs>
            <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridColor} vertical={false} />
          {isIntraday ? (
            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              stroke={chartColors.axisColor}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              minTickGap={40}
              tickFormatter={formatTime}
            >
              <Label value="Time (ET)" offset={-10} position="insideBottom" style={{ fill: chartColors.axisColor, fontSize: '12px' }} />
            </XAxis>
          ) : (
            <XAxis
              dataKey="date"
              stroke={chartColors.axisColor}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              minTickGap={30}
              tickFormatter={formatDateTick}
            >
              <Label value="Date" offset={-10} position="insideBottom" style={{ fill: chartColors.axisColor, fontSize: '12px' }} />
            </XAxis>
          )}
          <YAxis
            domain={[minPrice - padding, maxPrice + padding]}
            stroke={chartColors.axisColor}
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(val) => `$${val.toFixed(0)}`}
            width={50}
          >
            <Label value="Price" angle={-90} position="insideLeft" style={{ fill: chartColors.axisColor, fontSize: '12px', textAnchor: 'middle' }} />
          </YAxis>
          <Tooltip
            contentStyle={{ backgroundColor: chartColors.tooltipBg, borderColor: chartColors.tooltipBorder, color: chartColors.textColor, borderRadius: '12px' }}
            itemStyle={{ color: chartColors.textColor }}
            formatter={(value: number | undefined) => {
              if (value === undefined) return ['--', 'Price'];
              return [`$${value.toFixed(2)}`, 'Price'];
            }}
            labelFormatter={(label) => {
              if (isIntraday) {
                const d = new Date(label);
                return d.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'America/New_York',
                }) + ' ET';
              }
              return new Date(label).toLocaleDateString();
            }}
          />
          {isIntraday && marketLines && (
            <>
              <ReferenceLine
                x={marketLines.marketOpen}
                stroke="var(--color-accent-subtle)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: 'Open',
                  position: 'insideTopRight',
                  fill: 'var(--color-accent-subtle)',
                  fontSize: 11,
                  fontWeight: 500,
                  offset: 4,
                }}
              />
              <ReferenceLine
                x={marketLines.marketClose}
                stroke="var(--color-accent-subtle)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: 'Close',
                  position: 'insideTopLeft',
                  fill: 'var(--color-accent-subtle)',
                  fontSize: 11,
                  fontWeight: 500,
                  offset: 4,
                }}
              />
            </>
          )}
          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorPrice)"
          />
          {/* Lot purchase price lines */}
          {lotPriceLines.map((lot, idx) => (
            <ReferenceLine
              key={`lot-${idx}`}
              y={lot.price}
              stroke="#a1a1aa"
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{
                value: lot.label,
                position: 'insideTopRight',
                fill: '#a1a1aa',
                fontSize: 11,
                fontWeight: 600,
                offset: 5,
              }}
            />
          ))}
          {/* Weighted average price line */}
          {avgPriceLine !== undefined && (
            <ReferenceLine
              y={avgPriceLine}
              stroke="#f59e0b"
              strokeDasharray="6 3"
              strokeWidth={2}
              label={{
                value: 'Weighted Avg',
                position: 'insideTopRight',
                fill: '#f59e0b',
                fontSize: 11,
                fontWeight: 'bold',
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};
